<template>
  <div class="notification-bell">
    <Dropdown trigger="click" @on-click="handleDropdownClick">
      <Badge :count="unreadCount" :overflow-count="99">
        <Icon type="ios-notifications-outline" size="22" />
      </Badge>
      <DropdownMenu slot="list" style="width: 320px;">
        <div class="notification-header">
          <span>消息通知</span>
          <a @click.stop="markAllRead" v-if="unreadCount > 0">全部已读</a>
        </div>
        <div class="notification-body">
          <div v-if="loading" class="loading-state">
            <Spin />
          </div>
          <div v-else-if="notifications.length === 0" class="empty-state">
            <Icon type="ios-mail-outline" size="40" color="#c0c4cc" />
            <p>暂无新消息</p>
          </div>
          <div v-else>
            <div 
              v-for="item in notifications" 
              :key="item._id"
              class="notification-item"
              :class="{ unread: !item.isRead }"
              @click.stop="viewNotification(item)"
            >
              <div class="item-icon">
                <Icon :type="getIcon(item.type)" :color="getColor(item.type)" />
              </div>
              <div class="item-content">
                <div class="item-title">{{ item.title }}</div>
                <div class="item-desc">{{ truncate(item.content, 40) }}</div>
                <div class="item-time">{{ formatTime(item.createTime) }}</div>
              </div>
            </div>
          </div>
        </div>
        <DropdownItem name="viewAll" class="view-all-btn">
          查看全部消息
        </DropdownItem>
      </DropdownMenu>
    </Dropdown>
  </div>
</template>

<script>
import { getNotificationList, getUnreadCount, markAllAsRead, markAsRead } from '@/api/notification'

export default {
  name: 'NotificationBell',
  data() {
    return {
      loading: false,
      notifications: [],
      unreadCount: 0,
      pollingTimer: null
    }
  },
  methods: {
    async loadNotifications() {
      this.loading = true
      try {
        const res = await getNotificationList({
          page: 1,
          pageSize: 5,
          isRead: false
        })
        
        if (res.data && (res.data.success || res.data.code === 200)) {
          this.notifications = res.data.data?.list || []
        }
      } catch (error) {
        console.error('获取通知失败:', error)
      } finally {
        this.loading = false
      }
    },
    async loadUnreadCount() {
      try {
        const res = await getUnreadCount()
        if (res.data && (res.data.success || res.data.code === 200)) {
          this.unreadCount = res.data.data?.total || 0
        }
      } catch (error) {
        console.error('获取未读数量失败:', error)
      }
    },
    async markAllRead() {
      try {
        const res = await markAllAsRead()
        if (res.data && (res.data.success || res.data.code === 200)) {
          this.$Message.success('全部标为已读')
          this.unreadCount = 0
          this.notifications.forEach(n => n.isRead = true)
        }
      } catch (error) {
        this.$Message.error('操作失败')
      }
    },
    async viewNotification(item) {
      // 标记为已读
      if (!item.isRead) {
        try {
          await markAsRead([item._id])
          item.isRead = true
          this.unreadCount = Math.max(0, this.unreadCount - 1)
        } catch (error) {
          console.error('标记已读失败:', error)
        }
      }
      
      // 跳转到消息中心
      this.$router.push('/notifications')
    },
    handleDropdownClick(name) {
      if (name === 'viewAll') {
        this.$router.push('/notifications')
      }
    },
    startPolling() {
      // 每60秒轮询一次
      this.pollingTimer = setInterval(() => {
        this.loadUnreadCount()
      }, 60000)
    },
    stopPolling() {
      if (this.pollingTimer) {
        clearInterval(this.pollingTimer)
        this.pollingTimer = null
      }
    },
    getIcon(type) {
      const icons = {
        system: 'ios-information-circle',
        business: 'ios-document',
        alert: 'ios-warning',
        announcement: 'ios-megaphone'
      }
      return icons[type] || 'ios-mail'
    },
    getColor(type) {
      const colors = {
        system: '#409EFF',
        business: '#67C23A',
        alert: '#F56C6C',
        announcement: '#E6A23C'
      }
      return colors[type] || '#909399'
    },
    truncate(str, length) {
      if (!str) return ''
      return str.length > length ? str.substring(0, length) + '...' : str
    },
    formatTime(time) {
      if (!time) return ''
      const date = new Date(time)
      const now = new Date()
      const diff = now - date
      
      if (diff < 60 * 1000) return '刚刚'
      if (diff < 60 * 60 * 1000) return `${Math.floor(diff / (60 * 1000))}分钟前`
      if (diff < 24 * 60 * 60 * 1000) return `${Math.floor(diff / (60 * 60 * 1000))}小时前`
      
      return `${date.getMonth() + 1}/${date.getDate()}`
    }
  },
  mounted() {
    this.loadNotifications()
    this.loadUnreadCount()
    this.startPolling()
  },
  beforeDestroy() {
    this.stopPolling()
  }
}
</script>

<style lang="less" scoped>
.notification-bell {
  cursor: pointer;
  display: flex;
  align-items: center;
  
  .notification-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 12px 16px;
    border-bottom: 1px solid #f0f0f0;
    font-weight: 500;
    
    a {
      font-size: 12px;
      font-weight: normal;
      color: #409EFF;
    }
  }
  
  .notification-body {
    max-height: 300px;
    overflow-y: auto;
    
    .loading-state,
    .empty-state {
      padding: 40px 0;
      text-align: center;
      color: #c0c4cc;
      
      p {
        margin-top: 8px;
        font-size: 12px;
      }
    }
    
    .notification-item {
      display: flex;
      padding: 12px 16px;
      cursor: pointer;
      transition: background-color 0.2s;
      
      &:hover {
        background-color: #f5f7fa;
      }
      
      &.unread {
        background-color: #f0f7ff;
        
        .item-title {
          font-weight: 600;
        }
      }
      
      .item-icon {
        width: 32px;
        height: 32px;
        border-radius: 50%;
        background: #f5f5f5;
        display: flex;
        align-items: center;
        justify-content: center;
        margin-right: 12px;
        flex-shrink: 0;
      }
      
      .item-content {
        flex: 1;
        min-width: 0;
        
        .item-title {
          font-size: 13px;
          color: #303133;
          white-space: nowrap;
          overflow: hidden;
          text-overflow: ellipsis;
        }
        
        .item-desc {
          font-size: 12px;
          color: #909399;
          margin-top: 4px;
          white-space: nowrap;
          overflow: hidden;
          text-overflow: ellipsis;
        }
        
        .item-time {
          font-size: 11px;
          color: #c0c4cc;
          margin-top: 4px;
        }
      }
    }
  }
  
  .view-all-btn {
    text-align: center;
    border-top: 1px solid #f0f0f0;
    color: #409EFF;
    
    &:hover {
      background-color: #f5f7fa;
    }
  }
}
</style>

